import React from "react";
import PropTypes from "prop-types";
import styles from "./styles.module.css";
import Tooltip from "../shared-modules/Tooltip";

const Button = React.forwardRef(
  function Button({
    children,
    variant = "normal",
    size = "default",
    disabled = false,
    loading = false,
    type = "button",
    onClick,
    className = "",
    id,
    isRound = false,
    title,
    ...props
  }, ref) {
    // Create a string of class names using the CSS module
    const buttonClasses = [
      styles.button,
      styles[`button--${variant}`],
      styles[`button--${size}`],
      loading ? styles["button--loading"] : "",
      isRound ? styles["button--round"] : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const buttonElement = (
      <button
        {...props}
        ref={ref}
        type={type}
        className={buttonClasses}
        disabled={disabled || loading}
        onClick={onClick}
        aria-disabled={disabled || loading}
        id={id}
      >
        {loading ? <span className={styles.loader} /> : children}
      </button>
    );

    if (title) {
      return <Tooltip content={title}>{buttonElement}</Tooltip>;
    }

    return buttonElement;
  }
);

Button.propTypes = {
  /** The content to be displayed inside the button */
  children: PropTypes.node.isRequired,
  /** The variant of the button */
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "tertiary",
    "ghost",
    "normal",
    "danger",
  ]),
  /** The size of the button */
  size: PropTypes.oneOf(["small", "medium", "large", "default"]),
  /** Whether the button is disabled */
  disabled: PropTypes.bool,
  /** Whether the button is in a loading state */
  loading: PropTypes.bool,
  /** The type of the button */
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  /** Optional click handler */
  onClick: PropTypes.func,
  /** Optional className for additional styling */
  className: PropTypes.string,
  /** Whether the button should have rounded corners */
  isRound: PropTypes.bool,
  /** Optional title text to display in custom tooltip on hover */
  title: PropTypes.string,
};

Button.defaultProps = {
  variant: "primary",
  size: "default",
  disabled: false,
  loading: false,
  type: "button",
  className: "",
  isRound: false,
};

Button.displayName = "Button";

export default Button;
